import { createORPCClient, ORPCError, onError } from '@orpc/client';
import {
  ContractRouterClient,
  inferRPCMethodFromContractRouter,
  ValidationError,
} from '@orpc/contract';
import { ResponseValidationPlugin } from '@orpc/contract/plugins';
import { contractRouter } from '@erp/rpc';
import {
  API_BUILD_TIME_HEADER,
  UI_BUILD_HASH_HEADER,
  UI_BUILD_TIME_HEADER,
} from '@erp/shared-utils';

import { RPCLink } from '@orpc/client/fetch';
import config from 'src/config';
import { notify403 } from 'src/api/notify403';
import { showToastOutsideReact } from 'src/services/toastService';
import { showUpdateOverlayOutsideReact } from 'src/services/updateOverlayService';
import { createTanstackQueryUtils } from '@orpc/tanstack-query';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { buildPosthogHeaders } from 'src/api/posthogHeaders';
import { createRpcBatchLinkPlugin } from './batching';
import { isKeyedListCatalogCall } from './methodOverrides';
import { buildRpcUrl } from './rpcUrl';

function isOutputValidationError(error: unknown): boolean {
  return error instanceof ValidationError;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const AGGRESSIVE_NUDGE_THRESHOLD_MS = 3 * DAY_MS;

let versionMismatchToastShown = false;
let updateOverlayShown = false;

function showUpdateAvailableToast(): void {
  if (versionMismatchToastShown || updateOverlayShown) {
    return;
  }

  const shown = showToastOutsideReact({
    title: 'Update Available',
    message: 'A new version is available. Please refresh the page to continue.',
    color: 'warning',
    icon: 'sync',
    persistent: true,
    actionLabel: 'Refresh',
    onAction: () => window.location.reload(),
  });

  if (shown) {
    versionMismatchToastShown = true;
  }
}

function showUpdateRequiredOverlay(): void {
  if (updateOverlayShown) {
    return;
  }
  const shown = showUpdateOverlayOutsideReact();
  if (shown) {
    updateOverlayShown = true;
  }
}

const inferredRpcMethod = inferRPCMethodFromContractRouter(contractRouter);

/**
 * Create oRPC client with authentication integration
 */
const rpcLink = new RPCLink({
  url: buildRpcUrl(config.apiBaseUrl),

  // Use the same fetch configuration as existing API calls
  // This ensures cookies are included for authentication
  fetch: async (request: Request, init?: RequestInit) => {
    let headers = new Headers(init?.headers ?? request.headers);
    if (config.uiBuildHash) {
      headers.set(UI_BUILD_HASH_HEADER, config.uiBuildHash);
    }
    if (config.uiBuildTime) {
      headers.set(UI_BUILD_TIME_HEADER, config.uiBuildTime);
    }
    headers = buildPosthogHeaders(headers);

    const response = await fetch(request, {
      ...init,
      headers,
      credentials: 'include', // Include cookies for session auth
    });

    if (!updateOverlayShown) {
      const backendBuildTime = response.headers.get(API_BUILD_TIME_HEADER);
      const uiBuildTime = config.uiBuildTime;
      if (backendBuildTime && uiBuildTime) {
        const backendTimeMs = Date.parse(backendBuildTime);
        const uiTimeMs = Date.parse(uiBuildTime);
        if (!Number.isNaN(backendTimeMs) && !Number.isNaN(uiTimeMs)) {
          const diffMs = Math.abs(backendTimeMs - uiTimeMs);
          if (diffMs >= AGGRESSIVE_NUDGE_THRESHOLD_MS) {
            showUpdateRequiredOverlay();
          } else if (diffMs >= DAY_MS && !versionMismatchToastShown) {
            showUpdateAvailableToast();
          }
        }
      }
    }

    return response;
  },
  // The contract's inferred methods, with one override: a `listCatalog` call
  // carrying `keys` (the multiple-items filter, up to 10k resolved product
  // keys) always travels as POST — see methodOverrides.ts.
  method: (options, path, input) =>
    isKeyedListCatalogCall(path, input)
      ? 'POST'
      : inferredRpcMethod(options, path),

  plugins: [
    createRpcBatchLinkPlugin(),
    new ResponseValidationPlugin(contractRouter),
  ],

  interceptors: [
    onError((error) => {
      if (error instanceof ORPCError && error.code === 'FORBIDDEN') {
        notify403(error.message);
      }
      if (isOutputValidationError(error)) {
        showUpdateRequiredOverlay();
      }
      throw error;
    }),
  ],
});

/**
 * oRPC client instance
 * Provides access to all backend RPC procedures
 */
const client: ContractRouterClient<typeof contractRouter> =
  createORPCClient(rpcLink);
export const orpc = createTanstackQueryUtils(client);

/**
 * Direct RPC client for imperative calls
 */
export const rpcClient = client;

type oRPCType = typeof orpc;

/**
 * Hook to return a function that invalidates RPC queries. This is useful for invalidating queries
 * after a mutation or query. This hook is mostly for devex and to prevent code duplication.
 *
 * @example
 * const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [orpc.memoryBank.list.queryOptions().queryKey]);
 *
 * const { mutateAsync: createNote } = useCreateMemoryBankNote();
 *
 * const handleCreateNote = async (noteContent: string) => {
 *   await createNote(noteContent);
 *   invalidateRpcQueryFn();
 * };
 *
 * @param fn - The function to call to get the query keys to invalidate.
 * @returns A function that invalidates the RPC queries.
 */
export function useInvalidateRpcQueryFn<T extends Record<string, unknown>>(
  fn: (orpc: oRPCType, input: T) => QueryKey[],
  dependencies: React.DependencyList = [],
) {
  const queryClient = useQueryClient();
  const memoizedFn = useCallback(fn, [fn, ...dependencies]);

  return useCallback(
    (input: T = {} as T) => {
      memoizedFn(orpc, input).forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    },
    [memoizedFn, queryClient],
  );
}

/**
 * Router type for the RPC client
 * This will be properly typed once the backend router is available
 */
export type RpcRouter = typeof client;
